import {
    AfterViewInit,
    Component,
    EventEmitter,
    HostBinding,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChildren,
} from '@angular/core';
import { NgForOf, NgIf, NgStyle } from '@angular/common';
import { LegendButtons, SegmentLabels } from '@se/cp.common';
import { LegendButtonsConf } from '../legend/LegendButtons';
import { LegendClickEvent } from '../legend/legendClickEvent';
import { LegendComponent } from '../legend/legend.component';
import { MapComponent } from '../map/map.component';
import { IAnatomicMap, IMultistageAnatomicMap } from '../models/IAnatomicMap';
import { IColorCode } from '../models/IColorCode';
import { IColorData } from '../models/IColorData';
import { IContainerConfig } from '../models/IContainerConfig';
import { IFixedSize } from '../models/IFixedSize';
import { IMatrixLayout } from '../models/IMatrixLayout';
import { ISegmentItem, RegionId } from '../models/ISegmentItem';
import { ISelectionEvent } from '../models/ISelectionEvent';
import { FillBoardService } from '../services/fillBoardService/fill-board.service';
import { ImgDownloaderService } from '../services/imgDownloaderService/img-downloader.service';
import { RasterImgWrapper } from '../services/imgDownloaderService/RasterImgWrapper';

@Component({
    selector: 'map-container',
    templateUrl: './map-container.component.html',
    styleUrls: ['./map-container.component.scss'],
    standalone: true,
    imports: [MapComponent, LegendComponent, NgStyle, NgIf, NgForOf],
})
export class MapContainerComponent implements OnInit, OnChanges, AfterViewInit {
    @Input()
    public containerConfig: IContainerConfig;

    @Input()
    public buttonsConfiguration: LegendButtons[] = [];

    @Input()
    public data: Array<IColorData>;

    @Input()
    public legendColors: Array<IColorCode>;

    @Input()
    public readOnly = false;

    @Input()
    public segmentLabelType: SegmentLabels = SegmentLabels.none;

    @Input()
    public isHorizontal = false;

    @Input()
    public isWithTooltip = true;

    @Input()
    public isWithViewId = true;
    @Input()
    public fixedColumnNumber: number | null = null;
    @Input()
    public viewTopLabel: string | null = null;
    @Input()
    public viewBottomLabel: string | null = null;
    public viewsSideLenght = 61.5 * 3 + 10 + 4; // dimensione base della riga in SUITEESTENSA, impostiamo altezza minina 3 righe piu padding
    @Input()
    public fixedSize: IFixedSize = null;
    @Input()
    configuredRowNumber: number;
    @Output()
    public dataUpdate = new EventEmitter<IColorData[]>();
    public currentColor: IColorCode;
    public defaultColor: IColorCode;
    public selectedSegment: ISegmentItem = null;
    public buttonsConf: LegendButtonsConf = null;
    gridColumnsResponsiveLayout: string;
    gridRowResponsiveLayout: string;
    private _maxFixedColumnNumber: number;
    @ViewChildren(MapComponent)
    private allMaps: MapComponent[];
    private mapsTilesLayout: IMatrixLayout = {
        columnNumber: 0,
        rowNumber: 0,
    };

    constructor(
        private fillBoardSrv: FillBoardService,
        private imgDownloader: ImgDownloaderService,
    ) {}

    private _selectedMap: IMultistageAnatomicMap = null;

    public get selectedMap() {
        return this._selectedMap;
    }

    public set selectedMap(currentMapSelected) {
        this._selectedMap = currentMapSelected;
        this.onExternalMapSelChange(currentMapSelected);
    }

    public get getLegendStyle() {
        if (this.isHorizontal) {
            return {
                'grid-row': '2',
                height: this.fixedSize ? this.fixedSize.height * 0.25 + 'px' : 'unset',
            };
        } else {
            return {
                'grid-column': '2',
            };
        }
    }

    public get getGridListStyle() {
        const gap = 7;
        if (!!this.fixedColumnNumber && !!this._maxFixedColumnNumber) {
            return {
                display: 'grid',
                'box-sizing': 'border-box',
                'grid-template-columns': `repeat(${this._maxFixedColumnNumber},minmax(198.5px,1fr))`,
                'grid-template-rows': `repeat(auto-fit,minmax(${this.viewsSideLenght}px,1fr))`,
                'justify-content': 'unset',
                'overflow-x': 'auto',
                padding: '0',
                gap: gap + 'px',
            };
        } else {
            const maxRowDimension = this.configuredRowNumber ? this.configuredRowNumber * 61.5 - 28 + 'px' : '100%';
            const idealColGap = gap * (this.mapsTilesLayout.columnNumber - 1);
            const idealRowGap = gap * (this.mapsTilesLayout.rowNumber - 1);
            const totalColGap = gap * (this.containerConfig.maps.length - 1);
            const totalRowGap = gap * (this.containerConfig.maps.length - 1);
            const minValue = this.viewsSideLenght + 'px';
            const idealColValue = ` calc(100% / ${this.mapsTilesLayout.columnNumber}) - ${idealColGap}px`;
            const idealRowValue = ` calc( 100% / ${this.mapsTilesLayout.rowNumber}) - ${idealRowGap}px`;
            const maxColValue = ` calc(${maxRowDimension} - ${totalColGap}px)`;
            const maxRowValue = ` calc(${maxRowDimension} - ${totalColGap}px)`;
            this.gridColumnsResponsiveLayout = `repeat(auto-fill,minmax(clamp(${minValue},${idealColValue}, ${maxColValue}) ,1fr))`;
            this.gridRowResponsiveLayout = 'repeat(auto-fit, minmax(198.5px, 1fr))'; //`repeat(auto-fill,minmax(clamp(${minValue},${idealRowValue}, ${maxRowValue}) ,1fr))`;
            return {
                display: 'grid',
                'box-sizing': 'border-box',
                'grid-template-columns': `${this.gridColumnsResponsiveLayout}`,
                'grid-template-rows': `${this.gridRowResponsiveLayout}`,
                'justify-content': 'start',
                padding: '0',
                gap: gap + 'px',
            };
        }
    }

    @HostBinding('style')
    get styles() {
        if (this.isHorizontal) {
            return {
                display: 'grid',
                'grid-template-rows': 'auto min-content',
                gap: this.fixedSize?.gap ? this.fixedSize.gap + 'px' : '7px',
            };
        } else {
            return {
                display: 'grid',
                'grid-template-columns': 'auto min-content',
                gap: this.fixedSize?.gap ? this.fixedSize.gap + 'px' : '7px',
            };
        }
    }

    public ngOnInit() {
        if (this.legendColors) {
            this.currentColor = this.legendColors[0];
            this.defaultColor = this.legendColors.filter((col) => col.IsNormality)[0] ?? this.legendColors[0];
        }
        // per scollegare i dati dal wrapper
        if (this.data) {
            this.data = [...this.data];
        }

        this.buttonsConf = {
            normalColor: this.defaultColor,
            buttons: this.buttonsConfiguration,
        };
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.containerConfig || changes.data) {
            this.selectSegmentInView();
        }
        if (changes.containerConfig || changes.fixedSize || changes.fixedColumnNumber) {
            this.calcViewsSideL();
            this.calculateMaxGridDimension();
        }
    }

    ngAfterViewInit() {
        this.calcViewsSideL();
    }

    public onSelectionEvent(ev: ISelectionEvent): void {
        // ev.segment è presente se ho premuto su un segmento
        //      altrimenti ho premuto sulla mappa
        // ev.segment.isVisited ===true significa che è stato premuto sul segmento che è selezionato ( e va deselezionato)
        // ev.segment.isVisited === false significa che è stato premuto su quel segmento e che era deselezionato( e va selezionato)
        if (ev.segment) {
            this.handleSegmentSelection(ev.segment);
        } else {
            this.selectedSegment = null;
        }
        this.handleMapSelectionAfterSegmentSelectionChoosen(ev.originMap, ev.segment ? false : true);
    }

    public onDataUpdated(newData: IColorData) {
        const shallowCopiedData = { ...newData };
        // const index = this.data.findIndex((data) => data.regionID === newData.regionID);
        // this.data[index] =shallowCopiedData;
        this.dataUpdate.emit([shallowCopiedData]);
    }

    public onLegendSelection(code: IColorCode) {
        this.currentColor = {
            ...code,
        };

        this.handleSegmentSelection(this.selectedSegment);
    }

    public deselect() {
        this.selectedSegment = null;
        // this.handleSegmentSelection(this.selectedSegment);
    }

    public onLegendBtnClick(ev: LegendClickEvent) {
        switch (ev) {
            case LegendClickEvent.colorAll:
                this.updateAllMapColors('color', 'all');
                break;
            case LegendClickEvent.uncolorAll:
                this.updateAllMapColors('decolor', 'all');
                break;
            case LegendClickEvent.colorSelected:
                if (this.selectedMap) {
                    this.updateAllMapColors('color', 'selected');
                }
                break;
            case LegendClickEvent.uncolorSelected:
                if (this.selectedMap) {
                    this.updateAllMapColors('decolor', 'selected');
                }
                break;
            default:
                break;
        }
    }

    public getAllViews(): SVGElement[] {
        return this.allMaps.map((mapComp) => mapComp.svgMap);
    }

    // generare il base64 delle immagini da inviare al server
    public getImages(): Promise<RasterImgWrapper[]> {
        return Promise.all([
            this.imgDownloader.viewsImgCreator(this.getAllViews(), 'mappa', 1000, this.viewBottomLabel),
            this.imgDownloader.legendImgCreator(this.legendColors, 'legenda', 400),
        ]);
    }

    // metodo esposto richiato nel wrapper per

    public trackByIdentity(index: number, map: IAnatomicMap) {
        // possibile miglioria eliminando stageID
        return map.segmentCollection ? map.viewId + map.segmentCollection.length : null;
    }

    _isThisMapSelected(map: IMultistageAnatomicMap): boolean {
        // versione multiStageMap
        if (this.selectedMap?.stageID != undefined && map.stageID != undefined) {
            return (
                this.selectedMap && map.stageID === this.selectedMap.stageID && map.viewId === this.selectedMap.viewId
            );
        }

        // versione mappa singola
        return this.selectedMap && map.viewId === this.selectedMap.viewId;
    }

    private selectSegmentInView() {
        if (!!this.selectedSegment && !!this.selectedMap) {
            this.containerConfig.maps.forEach((map: IAnatomicMap) => {
                if (map.segmentCollection) {
                    const segmentToUpdate: ISegmentItem | undefined = map.segmentCollection.find(
                        (segment: ISegmentItem) => {
                            return segment.regionID === this.selectedSegment.regionID;
                        },
                    );
                    if (!!segmentToUpdate) {
                        segmentToUpdate.isVisited = true;
                    }
                }
            });
        }
    }

    private calcViewsSideL() {
        const numOfTiles = this.containerConfig?.maps?.length || 0;
        if (this.fixedSize && !this.fixedColumnNumber) {
            const realWidth = this.fixedSize.width * (this.isHorizontal ? 1 : 0.75);
            const realHeight = this.fixedSize.height * (!this.isHorizontal ? 1 : 0.75);
            this.viewsSideLenght = this.fillBoardSrv.getTileDimension({
                numOfTiles: numOfTiles,
                boardW: realWidth,
                boardH: realHeight,
                padding: this.fixedSize.padding,
                gap: this.fixedSize.gap,
            });
        } else {
            this.mapsTilesLayout = this.fillBoardSrv.getMatrixFromNum(numOfTiles);
        }
    }

    private handleSegmentSelection(eventSeg: ISegmentItem): void {
        // deselect if segment is already selected
        if (this.selectedSegment && this.selectedSegment.regionID === eventSeg.regionID) {
            this.selectedSegment = null;
        } else {
            this.selectedSegment = { ...eventSeg };
        }
    }

    private handleMapSelectionAfterSegmentSelectionChoosen(eventMap: IAnatomicMap, isDeselecting: boolean): void {
        if (
            this.selectedMap &&
            this.selectedMap.viewId === eventMap.viewId &&
            (!this.selectedSegment || isDeselecting)
        ) {
            this.selectedMap = null;
        } else {
            this.selectedMap = { ...eventMap };
            this.selectedMap.isVisited = true;
        }
    }

    private updateAllMapColors(action: 'color' | 'decolor', allOrSelected: 'all' | 'selected'): void {
        const regionIdsToUpdate = this.whichRegionsToUpdate(allOrSelected);
        this.data = this.modifyMultipleRegions(this.data, regionIdsToUpdate, action);
        const dataToUpdate: IColorData[] = this.data.filter((d) => regionIdsToUpdate.includes(d.regionID));
        this.dataUpdate.emit(dataToUpdate);
    }

    private whichRegionsToUpdate(allOrSelected: 'all' | 'selected'): RegionId[] {
        return allOrSelected === 'selected'
            ? this.selectedMap.segmentCollection.map((s) => s.regionID)
            : this.containerConfig.maps.reduce((regionIdToUpdate: RegionId[], map: IAnatomicMap) => {
                  const regionIdMapToUpdate: RegionId[] = map.segmentCollection.map(
                      (segmentItem: ISegmentItem) => segmentItem.regionID,
                  );
                  return regionIdToUpdate.concat(regionIdMapToUpdate);
              }, []);
    }

    private modifyMultipleRegions(
        datas: IColorData[],
        regionIdsToUpdate: string[],
        action: 'color' | 'decolor',
    ): IColorData[] {
        return datas.map((data) => {
            if (regionIdsToUpdate.includes(data.regionID)) {
                const updated =
                    action === 'color'
                        ? { ...this.currentColor, regionID: data.regionID }
                        : { ...this.defaultColor, regionID: data.regionID };
                return updated;
            }
            return data;
        });
    }

    private calculateMaxGridDimension() {
        if (this.fixedColumnNumber) {
            this._maxFixedColumnNumber = Math.min(this.fixedColumnNumber, this.containerConfig.maps.length);
        }
    }

    private columns(n: number): string {
        const base = '200px ';
        let result = '';
        while (n > 0) {
            result += base;
            n--;
        }
        return result.trim();
    }

    private onExternalMapSelChange(currentMapSelected: IAnatomicMap) {
        if (!currentMapSelected) {
            return;
        }
        if (!this._selectedMap && !!currentMapSelected && currentMapSelected.isVisited) {
            currentMapSelected.isVisited = false;
            return;
        }
        if (this._isThisMapSelected(currentMapSelected)) {
            currentMapSelected.isVisited = true;
        } else {
            currentMapSelected.isVisited = false;
        }
    }
}
